import {
  Text,
  Box,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Flex,
  FormControl,
  Input,
  Heading,
  FormLabel,
  useToast,
  TabList,
  Tab,
  Tabs,
  TabPanels,
  TabPanel,
  Switch,
  Select,
} from '@chakra-ui/react';
import { defaultIconSize, Icons } from '../shared/Icons';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import {
  Role,
  UpdateUserRequest,
  userApi,
  UserType,
} from '@texas/api/endpoints/userApi';
import { useEffect, useState } from 'react';
import { SubmitButton } from '../shared/form/SubmitButton';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { Controller, useForm } from 'react-hook-form';
import { ErrorLabel } from '../shared/ErrorLabel';
import { useActiveSession } from '@texas/hooks/useSession';
import { TexasSelect } from '../shared/form/TexasSelect';
import { ReactSelectOption } from '@texas/types';
import { request } from '@texas/utils/helpers/httpHelpers';
import { Trans, useTranslation } from 'react-i18next';
import { RoleIcon } from '../shared/RoleIcon';
import {
  compareEnum,
  convertToEnum,
  getEnumValues,
} from '@texas/utils/helpers/enumHelpers';
import {
  hasRole,
  internalRole,
  roleFriendlyName,
  userTypeFriendlyName,
} from '@texas/utils/helpers/roleHelpers';
import { userEvents } from './events';
import { reactEvents } from '@bridge/reactEvents';
import { ErrorDetails } from '../shared/alert/ErrorDetails';
import {
  fadeInRightAnimation,
  fadeInScaleAnimation,
} from '@texas/resources/animations/animations';
import { SharedDisclosureProps } from '../shared/types';
import { UserProfiles } from './keyAccountManager/UserProfiles';
import { RoleRestrict } from '../shared/RoleRestrict';
import { getUserTypeOption } from './shared';
import { timezones } from '@texas/resources/countries/timezones';
import { customersApi } from '@texas/api/endpoints/customersApi';
import { useMetadata } from '@texas/hooks/metadata/useMetadata';
import { resolveWeightUnit, weightUnits } from '@texas/resources/weightUnits';
import { AngularNavLink } from '../shared/AngularNavLink';

interface Props extends SharedDisclosureProps {
  id: number;
}

export function EditUser({ id, isOpen, onClose }: Props) {
  const { t } = useTranslation();
  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} closeOnOverlayClick={false}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('user.editUser')}</ModalHeader>
          <ModalCloseButton />
          <InnerModal id={id} onClose={onClose} />
        </ModalContent>
      </Modal>
    </>
  );
}

function InnerModal({ id, onClose }: { id: number; onClose: () => void }) {
  const toast = useToast();
  const { t } = useTranslation();
  const [searchInput, setSearchInput] = useState('');

  const {
    data,
    refetch,
    loading: fetchLoading,
  } = useApiResource(userApi.getUser);

  const {
    branchesData: [branches, getBranches, loadingBranches],
  } = useMetadata();

  const {
    data: customerList,
    refetch: refetchCustomerList,
    loading: customerListLoading,
  } = useApiResource(customersApi.getAll);

  useEffect(() => {
    refetchCustomerList();
  }, [refetchCustomerList]);

  const { request: updateRequest, error } = useApiRequest(userApi.updateUser);
  const { request: updateMeRequest, error: updateMeError } = useApiRequest(
    userApi.updateMe,
  );
  const session = useActiveSession();

  const {
    handleSubmit,
    register,
    control,
    formState: { errors, isSubmitting, isValid, dirtyFields },
    reset,
  } = useForm<UpdateUserRequest>();

  useEffect(() => {
    refetch(id);
    getBranches();
  }, [id, refetch, getBranches]);

  useEffect(() => {
    if (!data) return;

    reset({
      name: data.name,
      email: data.email,
      branchId: data.branchId,
      customerId: data.customerId,
      emailNotifications: data.emailNotifications,
      timeZone: data.timeZone,
      preferredWeightUnit: resolveWeightUnit(data.preferredWeightUnit),
      userType: convertToEnum(UserType, data.userType),
      betaFeatures: data.betaFeatures,
    });
  }, [data, reset]);

  const editingSelf = session.currentUser.id === id;

  const onSubmit = async (data: UpdateUserRequest) => {
    if (editingSelf) {
      await request(updateMeRequest, [data], (_) => {
        toast({
          title: t('general.updated'),
          status: 'success',
          isClosable: true,
        });
        userEvents.currentUserUpdated.dispatch();
        reactEvents.currentUserUpdated.dispatch();
        onClose();
      });
    } else {
      await request(updateRequest, [id, data], (_) => {
        toast({
          title: t('general.updated'),
          status: 'success',
          isClosable: true,
        });
        userEvents.userUpdated.dispatch();
        onClose();
      });
    }
  };
  return (
    <>
      <Tabs>
        <TabList>
          <Tab>{t('general.information')}</Tab>
          <RoleRestrict allow={[Role.SystemAdministrator]}>
            <Tab>{t('userProfile.userProfiles')}</Tab>
          </RoleRestrict>
        </TabList>

        <TabPanels>
          <TabPanel p={0}>
            {error && <ErrorDetails error={error} />}
            {updateMeError && <ErrorDetails error={updateMeError} />}
            <Box bg="texas.bg.800" _light={{ bg: 'gray.50' }}>
              <ModalBody>
                <Flex gap={2} alignItems="center" py={2}>
                  {data && (
                    <>
                      <RoleIcon
                        animation={fadeInScaleAnimation()}
                        userRole={convertToEnum(Role, data.role)}
                        boxSize={6}
                      />
                      <Flex flexDir="column" animation={fadeInRightAnimation()}>
                        <Text fontSize="xs" opacity={0.6}>
                          {editingSelf
                            ? t('user.youAre')
                            : t('user.thisUserIs')}
                        </Text>
                        <Heading fontSize="md">
                          {roleFriendlyName(convertToEnum(Role, data.role), t)}
                        </Heading>
                      </Flex>
                    </>
                  )}
                </Flex>
              </ModalBody>
            </Box>
            {data && (
              <form onSubmit={handleSubmit(onSubmit)}>
                <ModalBody as={Flex} flexDir="column" gap={2}>
                  <FormControl isInvalid={!!errors.email?.message}>
                    <FormLabel>{t('general.email')}</FormLabel>
                    <Input
                      type="email"
                      {...register('email', { required: true })}
                      variant="texas"
                    />
                    <ErrorLabel text={errors.email?.message} />
                  </FormControl>
                  <FormControl isInvalid={!!errors.name?.message}>
                    <FormLabel>{t('general.name')}</FormLabel>
                    <Input {...register('name')} variant="texas" />
                    <ErrorLabel text={errors.name?.message} />
                  </FormControl>
                  <FormControl>
                    <FormLabel>{t('user.preferredWeightUnit')}</FormLabel>
                    <Select
                      {...register('preferredWeightUnit')}
                      variant="texas"
                    >
                      {Object.values(weightUnits).map((unit) => (
                        <option key={unit.unit} value={unit.unit}>
                          {`${t(unit.labelKey)} (${unit.symbol})`}
                        </option>
                      ))}
                    </Select>
                    <Text variant="sub">
                      {t('user.preferredWeightUnitDescription')}
                    </Text>
                  </FormControl>
                  {internalRole(convertToEnum(Role, data.role)) && (
                    <>
                      <RoleRestrict allow={[Role.SystemAdministrator]}>
                        <Text
                          fontSize="sm"
                          color="gray.200"
                          _light={{ color: 'gray.600' }}
                        >
                          <Trans
                            i18nKey="user.loginMethodsDetailsNote"
                            components={{
                              detailsLink: (
                                <AngularNavLink
                                  to="users.detail"
                                  navobj={{ userId: id }}
                                  onClick={onClose}
                                />
                              ),
                            }}
                          />
                        </Text>
                      </RoleRestrict>
                      <FormControl>
                        <FormLabel>{t('general.timeZone')}</FormLabel>
                        <Controller
                          name="timeZone"
                          control={control}
                          render={({ field }) => (
                            <TexasSelect
                              {...field}
                              value={timezones
                                .map((x) => ({
                                  label: `${x.name} ${x.utc}`,
                                  value: x.zone,
                                }))
                                .find((x) => x.value === field.value)}
                              onChange={(value) =>
                                field.onChange(value?.value ?? null)
                              }
                              isClearable={true}
                              options={timezones.map<ReactSelectOption>(
                                (u) => ({
                                  label: `${u.name} ${u.utc}`,
                                  value: u.zone,
                                }),
                              )}
                            />
                          )}
                        />
                      </FormControl>
                      <FormControl>
                        <FormLabel>{t('user.belongsToBranch')}</FormLabel>
                        <Controller
                          name="branchId"
                          control={control}
                          render={({ field }) => (
                            <TexasSelect
                              {...field}
                              value={branches
                                .map((x) => ({ label: x.name, value: x.id }))
                                .find((x) => x.value === field.value)}
                              onChange={(value) =>
                                field.onChange(value?.value ?? null)
                              }
                              isClearable={true}
                              isLoading={loadingBranches}
                              options={branches.map<ReactSelectOption>((u) => ({
                                label: u.name,
                                value: u.id,
                              }))}
                            />
                          )}
                        />
                      </FormControl>

                      {hasRole(data.role, [
                        Role.InternalAdministrator,
                        Role.SystemAdministrator,
                      ]) && (
                        <FormControl>
                          <FormLabel>{t('user.userType.userType')}</FormLabel>
                          <Controller
                            name="userType"
                            control={control}
                            render={({ field }) => (
                              <TexasSelect
                                {...field}
                                value={getUserTypeOption(field.value, t)}
                                onChange={(value) =>
                                  field.onChange(value?.value ?? null)
                                }
                                isClearable={true}
                                options={getEnumValues(
                                  UserType,
                                ).map<ReactSelectOption>((x) => ({
                                  label: userTypeFriendlyName(x as UserType, t),
                                  value: x,
                                }))}
                              />
                            )}
                          />
                        </FormControl>
                      )}
                      <FormControl
                        display="flex"
                        alignItems="center"
                        pt={2}
                        gap={2}
                      >
                        <Controller
                          name="emailNotifications"
                          control={control}
                          render={({ field }) => (
                            <Switch
                              isChecked={field.value}
                              onChange={(e) => field.onChange(e.target.checked)}
                            />
                          )}
                        />
                        <FormLabel mb="0">
                          {t('user.emailNotifications')}
                        </FormLabel>
                      </FormControl>
                      <FormControl
                        display="flex"
                        alignItems="center"
                        pt={2}
                        gap={2}
                      >
                        <Controller
                          name="betaFeatures"
                          control={control}
                          render={({ field }) => (
                            <Switch
                              isChecked={field.value}
                              onChange={(e) => field.onChange(e.target.checked)}
                            />
                          )}
                        />
                        <FormLabel mb="0">{t('user.enableBeta')}</FormLabel>
                      </FormControl>
                    </>
                  )}
                  {compareEnum(Role, data.role, Role.Brand) && (
                    <FormControl>
                      <FormLabel>{t('general.brand')}</FormLabel>
                      <Controller
                        name="customerId"
                        control={control}
                        render={({ field }) => (
                          <TexasSelect
                            {...field}
                            required={true}
                            isLoading={customerListLoading || fetchLoading}
                            placeholder={t('concept.searchBrand')}
                            value={customerList
                              ?.map((customer) => ({
                                value: customer.id,
                                label: customer.name,
                              }))
                              .find((c) => c.value === field.value)}
                            onChange={(option) => {
                              field.onChange(option ? option.value : null);
                            }}
                            onInputChange={(e) => {
                              setSearchInput(e);
                            }}
                            options={
                              searchInput.length === 0
                                ? []
                                : (customerList ?? []).map<ReactSelectOption>(
                                    (c) => ({
                                      label: c.name,
                                      value: c.id,
                                    }),
                                  )
                            }
                            isClearable={true}
                          />
                        )}
                      />
                    </FormControl>
                  )}
                </ModalBody>
                <ModalFooter
                  bg="texas.bg.950"
                  borderBottomRadius="md"
                  as={Flex}
                  flexDir="column"
                  gap={4}
                  _light={{ bg: 'gray.50' }}
                >
                  {dirtyFields.email && (
                    <FormControl animation={fadeInScaleAnimation()}>
                      <FormLabel>
                        <Flex gap={2}>
                          <Icons.ShieldKey boxSize={defaultIconSize} />
                          <Text>{t('user.passwordRequiredEmail')}</Text>
                        </Flex>
                      </FormLabel>
                      <Input
                        type="password"
                        placeholder={t('user.enterYourPassword')}
                        {...register('providedPassword')}
                        variant="texas"
                      />
                    </FormControl>
                  )}
                  <SubmitButton
                    disabled={!isValid}
                    loading={fetchLoading || isSubmitting}
                  >
                    {t('general.update')}
                  </SubmitButton>
                </ModalFooter>
              </form>
            )}
          </TabPanel>
          <RoleRestrict allow={[Role.SystemAdministrator]}>
            <TabPanel p={0}>
              <UserProfiles userId={id} />
            </TabPanel>
          </RoleRestrict>
        </TabPanels>
      </Tabs>
    </>
  );
}
